import { useCachedApi, generateCacheKey } from "../cache";
import {
  fetchDashboardSnapshot,
  fetchLeaderboardSnapshot,
  type DashboardSnapshot,
  type LeaderBoardSnapshot,
} from "../dashboardSnapshot";
import { getEnrollments } from "../userEnrollments";
import { fetchUserChores } from "../userChores";
import { fetchUserTournaments } from "../userTournaments";
import { fetchUserAssignments } from "../userAssignments";
import { fetchUserPathData } from "../user";

export type PassedBelt = {
  id: number;
  levelName: string;
  name: string;
  completedAt: string;
  roleId: number;
  lessonsCompleteCount: number;
};

export type UserPathData = {
  user: any;
  passedBelts: PassedBelt[];
};

/**
 * Hook to fetch and cache dashboard snapshot data
 */
export const useDashboardSnapshot = () => {
  return useCachedApi<DashboardSnapshot>(
    generateCacheKey("fetchDashboardSnapshot"),
    fetchDashboardSnapshot,
    {
      refetchOnMount: true,
    }
  );
};

/**
 * Hook to fetch and cache leaderboard snapshot data
 */
export const useLeaderboardSnapshot = () => {
  return useCachedApi<LeaderBoardSnapshot>(
    generateCacheKey("fetchLeaderboardSnapshot"),
    fetchLeaderboardSnapshot,
    {
      staleTime: 2 * 60, // will update every 2 minutes with leaderboard changes
      refetchOnMount: true,
    }
  );
};

/**
 * Hook to fetch and cache user enrollments
 */
export const useEnrollments = (options: { useToast?: boolean } = {}) => {
  return useCachedApi(generateCacheKey("getEnrollments", options), () => getEnrollments(options), {
    refetchOnMount: true,
  });
};

/**
 * Hook to fetch and cache user chores (assessments)
 */
export const useUserChores = () => {
  return useCachedApi(generateCacheKey("fetchUserChores"), fetchUserChores, {
    staleTime: 3 * 60, // will update every 3 minutes with newly assigned chores
    refetchOnMount: true,
  });
};

/**
 * Hook to fetch and cache user tournaments
 */
export const useUserTournaments = () => {
  return useCachedApi(generateCacheKey("fetchUserTournaments"), fetchUserTournaments, {
    staleTime: 3 * 60, // will update every 3 minutes with newly assigned tournaments
    refetchOnMount: true,
  });
};

/**
 * Hook to fetch and cache user assignments
 */
export const useUserAssignments = () => {
  return useCachedApi(generateCacheKey("fetchUserAssignments"), fetchUserAssignments, {
    staleTime: 3 * 60, // will update every 3 minutes with newly assigned assignments
    refetchOnMount: true,
  });
};

/**
 * Hook to fetch and cache user achievements (completed paths/belts)
 */
export const useAchievements = () => {
  return useCachedApi<UserPathData>(generateCacheKey("fetchUserPathData"), fetchUserPathData, {
    refetchOnMount: true,
  });
};
